/**
 * Client-side field validation for Product create/edit forms.
 *
 * Mirrors the Pydantic constraints in backend/models/product.py exactly
 * (title min 3 / max 200, part_number min 1 / max 120, description min 1)
 * so a user sees the same rule before the request ever reaches the API.
 * Shared between the Sell wizard and the "My Listings" edit flow.
 */

export const PRODUCT_FIELD_LIMITS = {
  title: { min: 3, max: 200 },
  description: { min: 1 },
  part_number: { min: 1, max: 120 },
} as const;

export function validateRequiredText(
  value: string,
  limits: { min?: number; max?: number },
): string | undefined {
  const v = value.trim();
  if (!v) return "This field is required";
  if (limits.min !== undefined && v.length < limits.min) {
    const noun = limits.min === 1 ? "character" : "characters";
    return `Must be at least ${limits.min} ${noun}`;
  }
  if (limits.max !== undefined && v.length > limits.max) {
    return `Must be at most ${limits.max} characters`;
  }
  return undefined;
}

export function validateTitle(value: string): string | undefined {
  return validateRequiredText(value, PRODUCT_FIELD_LIMITS.title);
}

export function validatePartNumber(value: string): string | undefined {
  return validateRequiredText(value, PRODUCT_FIELD_LIMITS.part_number);
}

export function validateDescription(value: string): string | undefined {
  return validateRequiredText(value, PRODUCT_FIELD_LIMITS.description);
}

export function validatePrice(value: string): string | undefined {
  if (value.trim() === "") return "This field is required";
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "Enter a valid price";
  return undefined;
}

export function validateStockQuantity(value: string): string | undefined {
  if (value.trim() === "") return "This field is required";
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return "Enter a valid whole number";
  return undefined;
}
